import { cn } from '@/utils';
import { formatScoreHistoryBadge } from '@/domains/scoring/score-history-display-policy';
import { formatScoreHistoryDate, formatScoreHistoryTime } from '../ScoreHistoryCard.utils';
import { resolveScoreHistoryBadgeColor } from '@/domains/scoring/score-history-display-policy';
import { scoreBadgeStyles, scoreHistoryRowStyles } from '../ScoreHistoryCard.styles';
import { selectDisplayScoreValue } from '@/domains/scoring';
import { TScoreHistoryEntry } from '@/domains/scoring/score-history-policy';
import { TSimulationType } from '@/types';
import { useMemo } from 'react';

/**
 * Props for the ScoreHistoryCardItem component, which represents
 * a single entry in the score history list.
 */
interface ScoreHistoryCardItemProps {
  /** The score history entry to display */
  entry: TScoreHistoryEntry;
  /** The index of the entry in the list */
  index: number;
  /** The type of simulation, if applicable */
  simulationType?: TSimulationType;
  /** The total number of visible entries */
  visibleEntriesLength: number;
}

/**
 * Component representing a single score history entry within the ScoreHistoryCard.
 * It displays the date, time, and score information for a specific entry.
 */
export const ScoreHistoryCardItem: React.FC<ScoreHistoryCardItemProps> = ({
  entry,
  index,
  simulationType,
  visibleEntriesLength,
}) => {
  /** Date format; e.g. `'Jan 13'` */
  const formattedDate = useMemo(() => formatScoreHistoryDate(entry.date), [entry]);

  /** Time format; e.g. `'12:34pm'` */
  const formattedTime = useMemo(() => formatScoreHistoryTime(entry.date), [entry]);

  /**
   * Determine if the current entry is the last one in the visible
   * list to conditionally apply styles that remove the bottom border
   * for the last entry. This helps visually separate entries while
   * maintaining a clean look at the end of the list.
   */
  const isLastEntry = useMemo(
    () => index === visibleEntriesLength - 1,
    [index, visibleEntriesLength],
  );

  /**
   * Determine the color for the score badge based on the
   * score, max score, pass/fail status, and simulation type.
   * This function encapsulates the logic for deciding the badge color,
   * which helps keep the rendering code clean and focused on layout.
   */
  const scoreColor = useMemo(() => {
    const scoreValue = selectDisplayScoreValue({
      fallback: 0,
      score: entry.score,
      scorePercentage: entry.scorePercentage,
    });

    return resolveScoreHistoryBadgeColor({
      maxScore: entry.maxScore,
      passed: entry.passed,
      runtimeRatingCutoffs: entry.runtimeRatingCutoffs,
      score: scoreValue,
      simulationType,
    });
  }, [entry, simulationType]);

  /**
   * Format the score history entry into a user-friendly string
   * that includes the score, max score, and pass/fail status.
   */
  const formattedBadge = useMemo(() => {
    const scoreValue = selectDisplayScoreValue({
      fallback: 0,
      score: entry.score,
      scorePercentage: entry.scorePercentage,
    });

    return formatScoreHistoryBadge({
      maxScore: entry.maxScore,
      passed: entry.passed,
      score: scoreValue,
      simulationType,
    });
  }, [entry, simulationType]);

  /**
   * Define class names for the various elements in the score history card item.
   * This approach centralizes the class name definitions, making it easier to
   * manage and update styles in one place. It also improves readability by
   * giving meaningful names to the class combinations used in the JSX.
   */
  const classNames = {
    component: cn(scoreHistoryRowStyles, isLastEntry && 'border-b-0 pb-0'),
    dateText: 'text-neutral-black min-w-[105px]',
    dateTimeContainer: 'flex items-center gap-2 typography-body',
    scoreBadge: scoreBadgeStyles({ color: scoreColor }),
    timeText: 'text-neutral-gray-700 min-w-[65px]',
  };

  /**
   * Sub-component for rendering the date and time information. This helps break down the
   * main component into smaller, more focused pieces, improving readability and maintainability.
   */
  const DateTimeComponent = ({ date, time }: { date: string; time: string }) => (
    <div className={classNames.dateTimeContainer}>
      <div className={classNames.dateText}>{date}</div>
      <div className={classNames.timeText}>
        <time dateTime={time}>{time}</time>
      </div>
    </div>
  );

  /**
   * Sub-component for rendering the score badge. This encapsulates the logic for displaying
   * the score information in a visually distinct way, making it easier to manage the badge's
   * appearance and content separately from the date/time information.
   */
  const BadgeComponent = ({ badge, color }: { badge: string; color: string }) => {
    return (
      <div className={classNames.scoreBadge} data-color={color}>
        {badge}
      </div>
    );
  };

  // =============================================================
  // Render the score history card item with the
  // formatted date, time, and score badge.
  // =============================================================
  return (
    <div
      role="listitem"
      aria-label={`${formattedDate}, ${formattedTime}, ${formattedBadge}`}
      className={classNames.component}
      data-entry-id={entry.id}
    >
      <div className="flex w-full items-center justify-between gap-5">
        <DateTimeComponent date={formattedDate} time={formattedTime} />
        <BadgeComponent badge={formattedBadge} color={scoreColor} />
      </div>
    </div>
  );
};

ScoreHistoryCardItem.displayName = 'ScoreHistoryCardItem';

export default ScoreHistoryCardItem;
